// BreakdownPanel — shared expand/collapse + family-grouped drill-down rows.
//
// Extracted from course-dashboard.tsx where ChapterCard and TopicCard had
// ~65 lines of identical JSX for the breakdown section (expand button,
// family grouping by FAMILY_ORDER, row list with icon/label/count).
//
// The parent is responsible for computing row-level data (to target, count
// display, visibility) since ChapterStats and TopicStats have different shapes.
// The panel only handles expand/collapse state and rendering.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { FAMILY_ORDER, type CognitiveFamily } from '@examforge/shared/schemas';
import type { DictionaryKey } from '@examforge/shared/i18n';
import type { TFunction } from '../i18n/use-t.js';

/** Pre-computed display row — parent maps its stat type into this shape. */
export interface BreakdownRowDisplay {
  family: CognitiveFamily;
  icon: React.ReactNode;
  label: string;
  count: string;
  to: string | null;
  key: string;
}

/** i18n labels for cognitive families shown in the breakdown. */
const FAMILY_LABEL: Partial<Record<CognitiveFamily, DictionaryKey>> = {
  generative: 'dashboard.familyGenerative',
  discrimination: 'dashboard.familyDiscrimination',
  synthesis: 'dashboard.familySynthesis',
};

export function BreakdownPanel({
  rows,
  t,
}: {
  rows: readonly BreakdownRowDisplay[];
  t: TFunction;
}) {
  const [expanded, setExpanded] = useState(false);

  if (rows.length === 0) return null;

  return (
    <>
      <button
        type="button"
        className="chapter-card-expand-btn"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown size={12} aria-hidden="true" /> : <ChevronRight size={12} aria-hidden="true" />}
        {expanded ? t('dashboard.hideContents') : t('dashboard.showContents')}
        <span className="chapter-card-expand-count">· {rows.length}</span>
      </button>
      {expanded && (() => {
        // Group rows by family, sorted by FAMILY_ORDER.
        const byFamily = new Map<CognitiveFamily, BreakdownRowDisplay[]>();
        for (const row of rows) {
          const arr = byFamily.get(row.family) ?? [];
          arr.push(row);
          byFamily.set(row.family, arr);
        }
        const families = [...byFamily.keys()].sort(
          (a, b) => FAMILY_ORDER[a] - FAMILY_ORDER[b],
        );
        return (
          <div className="chapter-card-breakdown-groups">
            {families.map((family) => {
              const labelKey = FAMILY_LABEL[family];
              const familyRows = byFamily.get(family) ?? [];
              return (
                <div key={family} className={`chapter-card-family chapter-card-family-${family}`}>
                  {labelKey && (
                    <h5 className="chapter-card-family-label">{t(labelKey)}</h5>
                  )}
                  <ul className="chapter-card-breakdown">
                    {familyRows.map((row) => {
                      const inner = (
                        <>
                          <span className="chapter-card-breakdown-icon" aria-hidden="true">{row.icon}</span>
                          <span className="chapter-card-breakdown-label">{row.label}</span>
                          <span className="chapter-card-breakdown-count">{row.count}</span>
                        </>
                      );
                      return (
                        <li key={row.key}>
                          {row.to ? (
                            <Link to={row.to} className="chapter-card-breakdown-row is-link">{inner}</Link>
                          ) : (
                            <span className="chapter-card-breakdown-row">{inner}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        );
      })()}
    </>
  );
}
